






















































































import Vue, { PropType } from 'vue'
import { debounce } from 'debounce'
import Group from '@/types/Group.interface'
import Person from '@/types/Person.interface'
import GroupSearchResults from '@/components/GroupSearchResults.vue'
import PersonSearchResults from '@/components/PersonSearchResults.vue'
export default Vue.extend({
    components: { GroupSearchResults, PersonSearchResults },
    props: {
        exclude: {
            type: Boolean,
            default: false
        },
        excludeGroups: {
            type: Array,
            default: null
        },
        excludePersons: {
            type: Array,
            default: null
        },
        groups: {
            type: Array as PropType<Group[]>,
            default: null
        },
        persons: {
            type: Array as PropType<Person[]>,
            default: null
        }
    },
    data: () => ({
        //  Determines if our group search is expanded or not
        groupExpand: true,
        //  Our groups we are searching for
        groupResults: [],
        //  Determines if we have started searching for groups
        groupSearched: false,
        //  Determines if we display ALL group results
        groupViewMore: false,
        //  Keeps track of a loading state for our placeholder elements
        loading: false,
        //  Determines if our person search is expanded or not
        personExpand: true,
        //  Our persons we are searching for
        personResults: [],
        //  Determines if we have started searching for contacts
        personSearched: false,
        //  Our actual search text query
        searchtext: null,
        //  Container for our groups AND persons
        selected: [],
        //  Container for our selected groups we will send to
        selectedGroups: [],
        //  Container for our selected persons we will send to
        selectedPersons: [],
        //  The height of our wrapper
        headerHeight: 45
    }),
    async mounted() {
        const groups = this.groups || []
        const persons = this.persons || []

        //  Add our props to our selecteds
        this.selectedGroups.push(...groups)
        this.selectedPersons.push(...persons)

        //  Container for our persons
        const formattedPersons = []

        //  Format our persons to be used in our chips
        persons.forEach((x: Person) =>
            formattedPersons.push({
                id: x.id,
                object: 'Person',
                name: this.$options.filters.personFullNameOrPhone(x)
            })
        )

        //  Add to our total selected container
        this.selected.push(...[...groups, ...formattedPersons])
    },
    computed: {
        groupList(): Group[] {
            //  If we tried searching then display the search results
            if (this.groupSearched) return this.groupResults

            //  Else, just return our temporary recent groups
            return this.recentGroups
        },
        personList(): Person[] {
            //  If we tried searching then display the search results
            if (this.personSearched) return this.personResults

            //  Elsem just return our temporary recent persons
            return this.recentPersons
        },
        recentGroups(): Group[] {
            return this.$store.getters.groupsRecent
        },
        recentPersons(): Person[] {
            return this.$store.getters.personsRecent
        },
        searchProp: {
            get() {
                return this.searchtext
            },
            set: debounce(function (value: string) {
                //  Set our search text to keep track of it
                this.searchtext = value

                //  Search for both persons and groups
                this.search()
            }, 750)
        }
    },
    methods: {
        async groupsSearch(): Promise<void> {
            //  If search text does NOT exist, set searched to false
            if (!this.searchtext) {
                this.groupSearched = false
                this.groupResults = []
                return
            }

            //  Else, set our search to true
            this.groupSearched = true

            //  Get our group selector groups from the store
            const groups = this.$store.getters.groupSelectorGroups

            //  Expand our list
            this.groupExpand = true

            //  Our search method we want to run just compares the name to our search query
            const search = (group: Group) => {
                //  Convert the group name into lower case
                const name = group.name.toLowerCase()

                //  Convert our search query to lower case
                const query = this.searchtext.toLowerCase()

                //  Perform the search
                return name.includes(query)
            }

            //  Run our search on our groups
            const result = groups.filter((x: Group) => search(x))

            //  Set our groups
            this.groupResults = result
        },
        onItemRemoved(item: Group | Person): void {
            //  Adjust our height
            this.setHeaderHeight()

            //  Look for this item in our selected
            const idx = this.selected.findIndex((x: Group | Person) => x.id === item.id)

            //  If it doesn't exist for some reason then do nothing
            if (idx === -1) return

            //  Else, lets remove it from the selected
            this.selected.splice(idx, 1)

            //  Check if this is a group
            const isGroup = item.object === 'Group'

            //  If it's a group then alias our list to the group list, else do the person list
            const list = isGroup ? this.selectedGroups : this.selectedPersons

            //  Find this item in our list
            const listIdx = list.findIndex((x: Group | Person) => x.id === item.id)

            //  Once found, lets remove it from the list
            list.splice(listIdx, 1)

            //  Update our list based on if we removed a group or person
            if (isGroup) this.selectedGroups = list
            else this.selectedPersons = list
        },
        onGroupSelected(group: Group): void {
            //  Set our wrapper height
            this.setHeaderHeight()

            //  Check if this group already exists in our list
            const exists = this.selectedGroups.find((x: Group) => x.id === group.id)

            //  If it DOES exist, do nothing
            if (exists) return

            //  Add to our list of selected groups
            this.selectedGroups.push(group)

            //  Add to our selected (unlike person we don't format this since it already fits)
            this.selected.push(group)
        },
        onPersonSelected(person: Person): void {
            //  Set our wrapper height
            this.setHeaderHeight()

            //  Check if this person already exists in our list
            const exists = this.selectedPersons.find((x: Person) => x.id === person.id)

            //  If it DOES exist, do nothing
            if (exists) return

            //  Add to our list of selected persons
            this.selectedPersons.push(person)

            //  Create an entry of just id and name
            const entry = {
                id: person.id,
                object: 'Person',
                name: this.$options.filters.personFullNameOrPhone(person)
            }

            //  Add to our selected
            this.selected.push(entry)
        },
        async personsSearch(): Promise<void> {
            //  If search text does NOT exist, set searched to false
            if (!this.searchtext) {
                this.personSearched = false
                this.personResults = []
                return
            }

            //  Else, set our search to true
            this.personSearched = true

            //  Else, assemble the search payload
            const payload = { searchtext: this.searchtext, limit: 30 }

            //  Expand our list
            this.personExpand = true

            //  Else, sent the search request to the API
            const persons = await this.$store.dispatch('personsSearch', payload)

            //  Set our persons
            this.personResults = persons
        },
        popNewContact(): void {
            //  Our base new person model
            const newPerson = {
                name_given: null,
                name_family: null,
                phone_default: {
                    free_form_number: null,
                    default: true,
                    type: 'MOBILE'
                }
            }

            //  Pop the contact creation dialog
            this.$store.dispatch('dialogOpen', {
                type: 'APP',
                title: 'Add Contact',
                icon: 'mdi-account',
                color: 'primary',
                component: 'new-contact-selector',
                closeOnSubmit: true,
                props: {
                    model: newPerson,
                    withImport: true,
                    broadcastForm: true
                },
                callback: async (person: Person) => {
                    //  If we don't have a person then we want to navigate away
                    if (!person) {
                        //  Close this component so no popups
                        return this.$emit('close')
                    }

                    //  Else, we actually do have a person so lets auto assign
                    this.onPersonSelected(person)
                }
            })
        },
        popNewGroup(): void {
            //  Navigate to our csv import page
            this.$router.push(`/csv/import?new_group=true`)

            //  Close this component
            this.$emit('close')
        },
        async search(): Promise<void> {
            //  Start our loading state
            this.loading = true

            //  Wrap our searches in a promise so we can stop loading once both finish
            Promise.all([this.personsSearch(), this.groupsSearch()]).then(() => {
                //  Stop loading
                this.loading = false

                //  Open or close our Group search based on number of results
                this.groupExpand = this.groupSearched
                    ? this.groupResults.length > 0
                    : true

                //  Open or close our Person search based on number of results
                this.personExpand = this.personSearched
                    ? this.personResults.length > 0
                    : true
            })
        },
        setHeaderHeight(): void {
            setTimeout(() => {
                //  Grab our header height
                const elem = document.getElementsByClassName('search-header')[0]

                //  Set the wrapper to be the same size
                this.headerHeight = elem.clientHeight
            }, 250)
        },
        submit(): void {
            //  Create our response object
            const response = {
                groups: this.selectedGroups,
                persons: this.selectedPersons
            }

            //  Emit the submit method with this response to let our broadcast form know
            this.$emit('submit', response)
        }
    }
})
